/**
 * ModelCatalogTable — xAI model catalog (quill_xai_models) with a
 * rate-limited "Refresh from xAI" action.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Chip, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDistanceToNow } from 'date-fns';
import DataTable, { Column } from '../../common/DataTable';
import { colors, typography } from '../../../theme/designTokens';
import {
  ModelCatalogRow,
  ModelRefreshResponse,
  QuillApiError,
  quillApi,
} from '../../../services/quillAdminService';

const REFRESH_COOLDOWN_SECONDS = 30;

/** Best-effort extraction of a retry-after value from the error message.
 * QuillApiError only carries {message, status, code} — the backend's
 * `retry_after_seconds` field on a 429 body is not preserved structurally
 * by quillAdminService's `request()` (see its own comment on
 * `quillApi.models.refresh`), so this falls back to the fixed cooldown
 * when no number can be parsed out of the message text. */
function parseRetryAfterSeconds(message: string): number | null {
  const match = message.match(/(\d+)\s*s(ec(ond)?s?)?\b/i);
  if (match) return parseInt(match[1], 10);
  return null;
}

function formatPricing(row: ModelCatalogRow): string {
  if (row.pricing_amount === null || row.pricing_amount === undefined) return '—';
  const amount =
    row.pricing_amount < 1
      ? `$${row.pricing_amount.toFixed(4)}`
      : `$${row.pricing_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return row.pricing_unit ? `${amount} / ${row.pricing_unit}` : amount;
}

export interface ModelCatalogTableProps {
  catalog: ModelCatalogRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  meta: { last_synced_at: string | null; stale: boolean; sync_age_hours: number | null };
  onRefreshed: (resp: ModelRefreshResponse) => void;
  notify: (message: string, severity?: 'success' | 'info' | 'error' | 'warning') => void;
}

export const ModelCatalogTable: React.FC<ModelCatalogTableProps> = ({
  catalog,
  loading,
  error,
  onRetry,
  meta,
  onRefreshed,
  notify,
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const resp = await quillApi.models.refresh();
      onRefreshed(resp);
      const s = resp.sync;
      notify(
        s
          ? `Synced ${s.models_found} models (${s.models_inserted} new, ${s.models_updated} updated, ${s.models_deprecated} deprecated).`
          : 'Catalog refreshed.',
        'success'
      );
      startCooldown(REFRESH_COOLDOWN_SECONDS);
    } catch (e) {
      if (e instanceof QuillApiError && e.code === 'RATE_LIMITED') {
        const seconds = parseRetryAfterSeconds(e.message) ?? REFRESH_COOLDOWN_SECONDS;
        notify(`Refresh is rate-limited — try again in ${seconds}s.`, 'warning');
        startCooldown(seconds);
      } else {
        notify(e instanceof Error ? e.message : 'Refresh failed.', 'error');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const columns: Column<ModelCatalogRow>[] = [
    {
      id: 'model_id',
      label: 'Model ID',
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typography.fonts.mono,
            fontSize: typography.sizes.sm,
            opacity: row.deprecated_at ? 0.55 : 1,
          }}
        >
          {row.model_id}
        </Typography>
      ),
    },
    {
      id: 'tier',
      label: 'Tier',
      render: (row) =>
        row.tier ? (
          <Chip label={row.tier} size="small" sx={{ opacity: row.deprecated_at ? 0.55 : 1 }} />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ),
    },
    {
      id: 'modalities',
      label: 'Modalities',
      render: (row) => {
        const inputs = row.input_modalities ?? [];
        const outputs = row.output_modalities ?? [];
        if (inputs.length === 0 && outputs.length === 0) {
          return (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              —
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, opacity: row.deprecated_at ? 0.55 : 1 }}>
            {inputs.map((m) => (
              <Chip key={`in-${m}`} label={`in: ${m}`} size="small" variant="outlined" />
            ))}
            {outputs.map((m) => (
              <Chip key={`out-${m}`} label={`out: ${m}`} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
    },
    {
      id: 'pricing',
      label: 'Pricing',
      render: (row) => (
        <Typography variant="body2" sx={{ opacity: row.deprecated_at ? 0.55 : 1 }}>
          {formatPricing(row)}
        </Typography>
      ),
    },
    {
      id: 'deprecated',
      label: 'Status',
      render: (row) =>
        row.deprecated_at ? (
          <Chip label="Deprecated" size="small" color="error" />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
            Model Catalog
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {meta.last_synced_at
              ? `Last synced ${formatDistanceToNow(new Date(meta.last_synced_at), { addSuffix: true })}${
                  meta.stale ? ' • stale' : ''
                }`
              : 'Never synced'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          disabled={refreshing || cooldown > 0}
          onClick={handleRefresh}
          sx={{ textTransform: 'none', borderColor: colors.deepTeal[500], color: colors.deepTeal[500] }}
        >
          {cooldown > 0 ? `Available in ${cooldown}s` : refreshing ? 'Refreshing…' : 'Refresh from xAI'}
        </Button>
      </Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <DataTable<ModelCatalogRow>
          columns={columns}
          data={catalog}
          loading={loading}
          emptyMessage="No models in the catalog yet"
        />
      )}
    </Box>
  );
};

export default ModelCatalogTable;
